import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Music } from 'lucide-react';

const RhythmGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [combo, setCombo] = useState(0);
  const [notes, setNotes] = useState([]);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [bpm, setBpm] = useState(120);
  
  const gameLoopRef = useRef(null);
  const beatIntervalRef = useRef(null);

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setCombo(0);
    setCurrentBeat(0);
    setBpm(120);
    generateNotes();
    startBeat();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setCombo(0);
    setCurrentBeat(0);
    setBpm(120);
    setNotes([]);
  };

  const generateNotes = () => {
    const newNotes = [];
    const totalBeats = 32;
    
    for (let i = 0; i < totalBeats; i++) {
      if (Math.random() < 0.3) { // 30% chance of note
        newNotes.push({
          beat: i,
          hit: false,
          missed: false
        });
      }
    }
    
    setNotes(newNotes);
  };

  const startBeat = () => {
    const beatTime = (60 / bpm) * 1000; // Convert BPM to milliseconds
    beatIntervalRef.current = setInterval(() => {
      setCurrentBeat(prev => {
        const newBeat = prev + 1;
        
        // Check for missed notes
        notes.forEach(note => {
          if (note.beat === prev && !note.hit && !note.missed) {
            note.missed = true;
            setCombo(0);
            setScore(prev => Math.max(0, prev - 10));
          }
        });
        
        // Level up every 8 beats
        if (newBeat % 8 === 0) {
          setLevel(prev => prev + 1);
          setBpm(prev => Math.min(200, prev + 10));
        }
        
        // Check if game should end
        if (newBeat >= 32) {
          setGameOver(true);
          setIsPlaying(false);
          if (score > highScore) {
            setHighScore(score);
          }
          return 0;
        }
        
        return newBeat;
      });
    }, beatTime);
  };

  const handleNoteClick = (noteIndex) => {
    if (!isPlaying || gameOver) return;
    
    const note = notes[noteIndex];
    if (note.hit || note.missed) return;
    
    // Calculate timing accuracy
    const beatDiff = Math.abs(currentBeat - note.beat);
    let points = 0;
    
    if (beatDiff === 0) {
      points = 100; // Perfect timing
      setCombo(prev => prev + 1);
    } else if (beatDiff === 1) {
      points = 50; // Good timing
      setCombo(prev => prev + 1);
    } else {
      points = 25; // Poor timing
      setCombo(0);
    }
    
    note.hit = true;
    setScore(prev => prev + points);
    setNotes([...notes]);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  useEffect(() => {
    return () => {
      if (beatIntervalRef.current) {
        clearInterval(beatIntervalRef.current);
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = notes.length > 0 ? Math.round((notes.filter(n => n.hit).length / notes.length) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Music className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Rhythm Master
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Click the notes in rhythm with the beat! Build combos and master the timing.
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Beat {currentBeat + 1} of 32
            </h4>
            
            {/* Beat Display */}
            <div className="flex justify-center mb-6">
              <div className="grid grid-cols-8 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 rounded-full ${
                      (currentBeat % 8) === i 
                        ? 'bg-neon-green animate-pulse' 
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            {/* Notes Grid */}
            <div className="flex justify-center mb-6">
              <div className="grid grid-cols-8 gap-2">
                {Array.from({ length: 32 }).map((_, i) => {
                  const note = notes.find(n => n.beat === i);
                  return (
                    <motion.button
                      key={i}
                      onClick={() => note && handleNoteClick(notes.indexOf(note))}
                      className={`w-8 h-8 rounded ${
                        !note 
                          ? 'bg-dark-100 border border-gray-600' 
                          : note.hit 
                            ? 'bg-neon-green' 
                            : note.missed 
                              ? 'bg-neon-red' 
                              : 'bg-neon-blue hover:bg-neon-blue/80 cursor-pointer'
                      }`}
                      whileHover={note && !note.hit && !note.missed ? { scale: 1.2 } : {}}
                      whileTap={note && !note.hit && !note.missed ? { scale: 0.8 } : {}}
                    >
                      {note && (note.hit ? '✓' : note.missed ? '✗' : '●')}
                    </motion.button>
                  );
                })}
              </div>
            </div>
            
            {/* Combo Display */}
            <div className="text-center">
              <p className="text-gray-400 font-tech">Combo:</p>
              <p className="text-2xl font-cyber font-bold text-neon-yellow">{combo}</p>
            </div>
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">BPM</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{bpm}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Accuracy</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">{accuracy}%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Combo</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">{combo}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            Rhythm Complete! 🎵
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Accuracy: {accuracy}% | Max Combo: {combo} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-green text-neon-green hover:bg-neon-green hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Objective</p>
              <p>Click notes in rhythm with the beat</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Scoring</p>
              <p>Perfect timing = 100pts, Good = 50pts, Poor = 25pts</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Combo</p>
              <p>Build combos for bonus points</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Progression</p>
              <p>BPM increases every 8 beats</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RhythmGame;
